import { useEffect, useState, useCallback, useRef } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.jsx';
import { api } from '../lib/api.js';
import { Avatar, Modal, Empty, useToast } from './ui.jsx';
import Icon from './Icon.jsx';
import { useTheme } from '../lib/hooks.js';
import { useRealtime, useRealtimeEvent } from '../lib/RealtimeContext.jsx';
import { relativeTime } from '../lib/format.js';

/**
 * Primary navigation per role. The same list drives the desktop rail, the
 * mobile tab bar and the account menu, so a destination can never appear in one
 * and go missing from another.
 */
const NAV = {
  patient: [
    { to: '/', label: 'Home', icon: 'home', end: true },
    { to: '/search', label: 'Find care', icon: 'search' },
    { to: '/bookings', label: 'Bookings', icon: 'clipboard' },
    { to: '/profile', label: 'Profile', icon: 'user' },
  ],
  nurse: [
    { to: '/nurse', label: 'Dashboard', icon: 'chart', end: true },
    { to: '/nurse/bookings', label: 'Bookings', icon: 'clipboard' },
    { to: '/nurse/availability', label: 'Schedule', icon: 'calendar' },
    { to: '/nurse/earnings', label: 'Earnings', icon: 'wallet' },
    { to: '/nurse/profile', label: 'Profile', icon: 'user' },
  ],
  admin: [
    { to: '/admin', label: 'Dashboard', icon: 'chart', end: true },
    { to: '/admin/verifications', label: 'Verify', icon: 'shield-check' },
    { to: '/admin/users', label: 'Users', icon: 'users' },
    { to: '/admin/bookings', label: 'Bookings', icon: 'clipboard' },
    { to: '/admin/complaints', label: 'Complaints', icon: 'megaphone' },
    { to: '/admin/services', label: 'Services', icon: 'toolbox' },
  ],
};

/** Captures the beforeinstallprompt event so we can offer an Install button. */
function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(
    () => window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true
  );

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  }, [deferred]);

  return { canInstall: !!deferred && !installed, install };
}

/**
 * `navigator.onLine` can read false while the browser's network service is still
 * starting, and the corresponding 'online' event may fire before React mounts —
 * which would leave the banner stuck on. So the flag is only trusted as a hint:
 * a live server stream is treated as proof we are connected.
 */
function useOnline() {
  const [flag, setFlag] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setFlag(true);
    const down = () => setFlag(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    // Re-read once the first frame has painted, in case we mounted mid-startup.
    const t = setTimeout(() => setFlag(navigator.onLine), 1200);
    return () => {
      clearTimeout(t);
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return flag;
}

/** Drives the top bar's elevation: flat at rest, shadowed once content scrolls under it. */
function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
}

function NotificationPanel({ onClose, items, onRead, onReadAll }) {
  const navigate = useNavigate();
  return (
    <Modal
      title="Notifications"
      onClose={onClose}
      bodyClass={items.length ? 'flush' : ''}
      footer={
        items.some((n) => !n.is_read) && (
          <button className="btn btn-ghost btn-sm" onClick={onReadAll}>
            <Icon name="check" /> Mark all as read
          </button>
        )
      }
    >
      {items.length === 0 ? (
        <Empty art="inbox" title="You're all caught up">
          Booking updates, confirmations and reminders will show up here.
        </Empty>
      ) : (
        items.map((n) => (
          <button
            key={n.id}
            type="button"
            className="list-row"
            style={{
              width: '100%',
              textAlign: 'left',
              background: n.is_read ? 'transparent' : 'var(--accent-soft)',
              border: 'none',
              borderBottom: '1px solid var(--border)',
              cursor: n.link ? 'pointer' : 'default',
              font: 'inherit',
              color: 'inherit',
            }}
            onClick={() => {
              onRead(n.id);
              if (n.link) {
                onClose();
                navigate(n.link);
              }
            }}
          >
            <span className={`icon-tile ${n.is_read ? 'icon-tile-neutral' : ''}`}>
              <Icon name="bell" />
            </span>
            <div className="grow">
              <div className="list-title">{n.title}</div>
              {n.body && <div className="list-sub">{n.body}</div>}
              <div className="tiny dim mt-1">{relativeTime(n.created_at)}</div>
            </div>
            {!n.is_read && (
              <span
                aria-label="Unread"
                style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', flex: 'none' }}
              />
            )}
          </button>
        ))
      )}
    </Modal>
  );
}

const THEME_CYCLE = { system: 'light', light: 'dark', dark: 'system' };
const THEME_ICON = { system: 'contrast', light: 'sun', dark: 'moon' };
const THEME_LABEL = { system: 'System theme', light: 'Light theme', dark: 'Dark theme' };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const online = useOnline();
  const scrolled = useScrolled();
  const toast = useToast();
  const { canInstall, install } = useInstallPrompt();
  const [theme, setTheme] = useTheme();
  const { unread: chatUnread, status: streamStatus } = useRealtime();
  // A live stream beats a stale onLine flag as evidence of connectivity.
  const offline = !online && streamStatus !== 'open';

  // A message that arrives while you are somewhere else announces itself and
  // deep-links to the conversation.
  useRealtimeEvent('message', (msg) => {
    if (msg.self || window.location.pathname.endsWith(`/bookings/${msg.booking_id}`)) return;
    toast.push(
      `${msg.sender_name}: ${msg.body.length > 70 ? `${msg.body.slice(0, 70)}…` : msg.body}`,
      'info',
      7000
    );
    if (navigator.vibrate) navigator.vibrate(25);
  });

  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const seenIds = useRef(null);

  // Every route change starts at the top of the page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [location.pathname]);

  const loadNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      seenIds.current = null;
      return;
    }
    try {
      const list = await api.get('/api/notifications');
      // Anything that arrived since the last poll is surfaced as a toast, so a
      // nurse sitting on the dashboard learns about a new request immediately.
      if (seenIds.current) {
        const fresh = list.filter(
          (n) =>
            !n.is_read &&
            !seenIds.current.has(n.id) &&
            // The page a notification points at announces the change itself.
            n.link !== window.location.pathname
        );
        for (const n of fresh.slice(0, 3)) toast.push(`${n.title} — ${n.body || ''}`.trim(), 'info', 7000);
      }
      seenIds.current = new Set(list.map((n) => n.id));
      setNotifications(list);
    } catch {
      /* offline — keep whatever we have */
    }
  }, [user, toast]);

  useEffect(() => {
    loadNotifications();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') loadNotifications();
    }, 20000);
    return () => clearInterval(t);
  }, [loadNotifications, location.pathname]);

  const unread = notifications.filter((n) => !n.is_read).length;
  const links = NAV[user?.role] || NAV.patient;

  const markRead = async (id) => {
    setNotifications((list) => list.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
    try {
      await api.post(`/api/notifications/${id}/read`);
    } catch {
      /* best effort */
    }
  };
  const markAllRead = async () => {
    setNotifications((list) => list.map((n) => ({ ...n, is_read: 1 })));
    try {
      await api.post('/api/notifications/read-all');
    } catch {
      /* best effort */
    }
  };

  const homeFor = user?.role === 'nurse' ? '/nurse' : user?.role === 'admin' ? '/admin' : '/';

  return (
    <div className="app">
      {offline && (
        <div className="offline-bar">
          <Icon name="wifi-off" />
          Offline — showing your last saved data
        </div>
      )}

      <header className="topbar" data-scrolled={scrolled ? 'true' : 'false'} style={offline ? { marginTop: 28 } : undefined}>
        <div className="topbar-inner">
          <Link to={homeFor} className="brand">
            <span className="brand-mark" aria-hidden="true">
              <Icon name="plus" strokeWidth={3} />
            </span>
            <span>
              Heal<em>At</em>Home
            </span>
          </Link>

          <nav className="nav-links" aria-label="Primary">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end}>
                <Icon name={l.icon} />
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="topbar-actions">
            {canInstall && (
              <button className="btn btn-sm" onClick={install} title="Install HealAtHome">
                <Icon name="download" /> Install
              </button>
            )}
            <button
              className="icon-btn"
              onClick={() => setTheme(THEME_CYCLE[theme])}
              title={`${THEME_LABEL[theme]} — switch to ${THEME_CYCLE[theme]}`}
              aria-label={`Switch theme, currently ${theme}`}
            >
              <Icon name={THEME_ICON[theme]} />
            </button>
            {user && (
              <button
                className="icon-btn"
                onClick={() => setShowNotifications(true)}
                aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
              >
                <Icon name="bell" />
                {unread > 0 && <span className="badge-dot">{unread > 9 ? '9+' : unread}</span>}
              </button>
            )}
            {user ? (
              <div style={{ position: 'relative' }}>
                <button
                  className="avatar-btn"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label="Account menu"
                  aria-expanded={menuOpen}
                  aria-haspopup="menu"
                >
                  <Avatar name={user.name} avatar={user.avatar} size="sm" />
                </button>
                {menuOpen && (
                  <>
                    <div className="menu-scrim" onClick={() => setMenuOpen(false)} />
                    <div className="menu-pop" role="menu">
                      <div className="menu-user">
                        <div className="row-tight">
                          <Avatar name={user.name} avatar={user.avatar} size="sm" />
                          <div className="grow">
                            <div className="bold truncate">{user.name}</div>
                            <div className="tiny dim truncate">{user.email}</div>
                          </div>
                        </div>
                        <span className="pill pill-accent" style={{ marginTop: 10 }}>
                          {user.role}
                        </span>
                      </div>
                      {links.map((l) => (
                        <Link key={l.to} to={l.to} className="menu-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                          <Icon name={l.icon} />
                          <span className="grow">{l.label}</span>
                        </Link>
                      ))}
                      <button
                        className="menu-item danger"
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          logout();
                          navigate('/login');
                        }}
                      >
                        <Icon name="logout" />
                        <span className="grow">Sign out</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn btn-sm btn-ghost">
                  Sign in
                </Link>
                <Link to="/register" className="btn btn-sm btn-primary">
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="main">{children}</main>

      {user && (
        <nav className="tabbar" aria-label="Primary">
          <div className="tabbar-inner">
            {links.slice(0, 5).map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end}>
                <span className="tab-icon">
                  <Icon name={l.icon} />
                </span>
                <span>{l.label}</span>
                {l.label === 'Bookings' && chatUnread.total > 0 && (
                  <span className="tab-dot" aria-label={`${chatUnread.total} unread messages`} />
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      {showNotifications && (
        <NotificationPanel
          items={notifications}
          onClose={() => setShowNotifications(false)}
          onRead={markRead}
          onReadAll={markAllRead}
        />
      )}
    </div>
  );
}
